import { useEffect, useState } from "react";

import { t } from "../../i18n";
import { markDismissed, PUSH_NUDGE, wasDismissed } from "../../lib/dismissal";
import { isStandalone } from "../../lib/platform";
import {
  DROPPED_PUSH_KEY,
  enablePush,
  getPushState,
  type PushState,
} from "../../lib/push";
import { useSession } from "../../session";

/** When this device first saw the dropped-push note. */
const DROPPED_SEEN_KEY = "roof-hawk:push-dropped-seen";
const RESTORE_GRACE_MS = 24 * 60 * 60 * 1000;

/**
 * True while a restore of a dropped subscription is plausibly under way. Only
 * a successful restore clears the note, so a restore that keeps failing would
 * otherwise hide the banner for good while push stays off. The note is
 * trusted for a day from the first start that saw it (issue #799).
 */
function restorePending(): boolean {
  try {
    // Truthy, as restoreDroppedPush reads it: an empty note restores nothing.
    if (!localStorage.getItem(DROPPED_PUSH_KEY)) {
      localStorage.removeItem(DROPPED_SEEN_KEY);
      return false;
    }
    const now = Date.now();
    const seen = Number(localStorage.getItem(DROPPED_SEEN_KEY));
    if (!(seen > 0)) {
      localStorage.setItem(DROPPED_SEEN_KEY, String(now));
      return true;
    }
    return now - seen < RESTORE_GRACE_MS;
  } catch {
    return false;
  }
}

/**
 * Offers notifications once the app is installed.
 *
 * Only inside the installed app, which is both a technical and an ordering
 * decision: iOS cannot subscribe from a browser tab at all, and asking someone
 * to install and to allow notifications in the same breath is two decisions at
 * once. Install first, then this.
 *
 * The permission dialog is opened straight from the click. Browsers only allow
 * it in response to a real gesture, so it cannot be moved into an effect or
 * behind an await of our own.
 */
export function NotificationNudge() {
  const { profileId } = useSession();
  const [state, setState] = useState<PushState | null>(null);
  const [dismissed, setDismissed] = useState(() => wasDismissed(PUSH_NUDGE));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isStandalone()) return;
    // Asset recovery dropped this device's subscription and left a note, so
    // RestoreDroppedPush is putting it back in the background. The state reads
    // "off" until it finishes, and a banner then would offer what is already
    // on its way back (#774). The next start reads the state afresh, and a
    // note more than a day old no longer counts (#799).
    if (restorePending()) return;

    let alive = true;
    void getPushState().then((current) => {
      if (alive) setState(current);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Anything other than "off" means there is nothing to offer: already on,
  // blocked in browser settings, unsupported, or not configured. The
  // Notifications screen in Tools explains those; a banner cannot.
  if (dismissed || state !== "off") return null;

  async function turnOn() {
    if (!profileId) {
      setError(t("Your login is not linked to a staff record yet."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setState(await enablePush(profileId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function dismiss() {
    markDismissed(PUSH_NUDGE);
    setDismissed(true);
  }

  return (
    <div className="updatebar" role="status">
      <span>{error ?? t("Turn on notifications for this device?")}</span>
      <button
        className="btn-primary"
        disabled={busy}
        onClick={() => void turnOn()}
      >
        {busy ? t("Asking…") : t("Turn on")}
      </button>
      <button className="btn-ghost" onClick={dismiss}>
        {t("Not now")}
      </button>
    </div>
  );
}
